/* eslint-disable import/no-unresolved */
import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import { useMediaQuery } from 'react-responsive';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import 'pure-react-carousel/dist/react-carousel.es.css';
import useScreen from 'hooks/useScreen';
import useIsBrowser from 'hooks/useIsBrowser';

// TODO: Investigate whether SSR is supported in V8 nuka-carousel
const Carousel = loadable(() => import('./WrappedCarousel'));

// TODO: Investigate why forwardRef is needed here
const ForwardRefCarousel = React.forwardRef((props, ref) => (
  <Carousel carouselRef={ref} {...props} />
));

// TODO: Investigate whether these touch handlers are necessary
let firstClientX;
let clientX;

const preventTouch = e => {
  const minValue = 5; // threshold

  clientX = e.touches[0].clientX - firstClientX;

  // Vertical scrolling does not work when you start swiping horizontally.
  if (Math.abs(clientX) > minValue) {
    e.preventDefault();
    e.returnValue = false;

    return false;
  }
  return null;
};

const touchStart = e => {
  firstClientX = e.touches[0].clientX;
};

const NewSliderComponent = props => {
  const containerRef = useRef(null);
  const carouselRef = useRef(null);

  // TODO: Refactor problematic initial render logic
  const isBrowser = useIsBrowser();

  const { isMobile, isDesktop } = useScreen();

  const isLargeDesktopDevice = useMediaQuery({
    query: '(min-width: 1281px)',
  });

  // TODO: This causes layoutshift on initial load. Slides appear large before shrinking
  // Assume this is an xs mobile device by default, until mounted in browser
  const isLargeDesktop = isBrowser ? isLargeDesktopDevice : false;
  const isMobileTabletOrSmallDesktop = !isLargeDesktop;

  // TODO: Destructure props in parameters
  const {
    children,
    deviceOverride = false,
    disableSliding,
    fabricGridControls = false,
    finalVisibleSlides,
    isMobileMaterialSwatches = false,
    navType,
    // We'll manage slideIndex state internally
    variant,
    fullHeight = false,
  } = props;

  const [slideIndex, setSlideIndex] = React.useState(0);

  const showLeftAndRightPartially =
    finalVisibleSlides < 2 && finalVisibleSlides > 1;

  // TODO: Investigate and refactor touch handlers
  useEffect(() => {
    const currentRef = containerRef.current;
    if (containerRef.current && !isMobile) {
      currentRef.addEventListener('touchstart', touchStart, {
        passive: true,
      });
      currentRef.addEventListener('touchmove', preventTouch, {
        passive: true,
      });
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('touchstart', touchStart, {
          passive: true,
        });
        currentRef.removeEventListener('touchmove', preventTouch, {
          passive: true,
        });
      }
    };
  }, [containerRef, isMobile]);

  // TODO: Investigate and refactor or remove dimension setting logic
  useEffect(() => {
    if (isMobile || !carouselRef.current) {
      return;
    }
    if (!isMobile && carouselRef.current !== null) {
      setTimeout(() => {
        carouselRef.current?.setDimensions?.();
      }, 0); // seems to fix initial height issues for nuka carousel
    }
  }, [carouselRef, isMobile, showLeftAndRightPartially]);

  const finalSlides = children;

  // TODO: Refactor visibleSlides calculation logic to be simpler
  let visibleSlides = 0;

  // Responsive defaults only overridden if values are set
  if (isLargeDesktop) {
    visibleSlides =
      finalVisibleSlides && finalVisibleSlides !== 4
        ? finalVisibleSlides
        : finalSlides.length < 4
        ? finalSlides.length
        : 4;
  }

  if (isMobileTabletOrSmallDesktop) {
    visibleSlides = finalSlides.length < 3 ? finalSlides.length : 3;
  }

  if (isMobile) {
    visibleSlides = finalSlides.length < 2 ? finalSlides.length : 2;
  }

  if (deviceOverride) {
    visibleSlides = finalVisibleSlides;
  }

  if (!finalSlides.length) {
    return (
      <div
        className={classNames(
          'mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover components [&_.slider-control-bottomcenter]:w-full',
          {
            '!mt-0': variant === 'material-swatches-sidebar',
            '!min-h-[250px] max-[400px]:!min-h-[400px]':
              variant === 'blog-slider',
          }
        )}
        ref={containerRef}
      />
    );
  }

  const hideNav = visibleSlides >= finalSlides.length || navType === '';

  // TODO: Refactor JSX to be leaner

  return (
    <div
      className={classNames(
        'NewSliderComponent components [&_.slider-control-bottomcenter]:w-full',
        {
          '[&_.slider-control-centerleft]:items-stretch [&_.slider-control-centerleft]:!bottom-0 [&_.slider-control-centerleft]:flex [&_.slider-control-centerleft]:justify-stretch [&_.slider-control-centerleft]:absolute [&_.slider-control-centerleft]:!top-0 [&_.slider-control-centerleft]:![transform:none] [&_.slider-control-centerleft]:!w-[calc(9%_-_9px)] [&_.slider-control-centerright]:items-stretch [&_.slider-control-centerright]:!bottom-0 [&_.slider-control-centerright]:flex [&_.slider-control-centerright]:justify-stretch [&_.slider-control-centerright]:absolute [&_.slider-control-centerright]:!top-0 [&_.slider-control-centerright]:![transform:none] [&_.slider-control-centerright]:!w-[calc(9%_-_9px)]': fabricGridControls,
          'mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover [&_.slider-control-centerleft]:!left-0 [&_.slider-control-centerright]:!right-0': !isMobileMaterialSwatches,
          '!mt-0': variant === 'material-swatches-sidebar',
          'pt-1 !mt-0': variant === 'blog-slider',
        }
      )}
      ref={containerRef}
    >
      {variant === 'blog-slider' && !isMobile && !hideNav && (
        <div className="w-full flex justify-end gap-2 mb-2">
          <button
            type="button"
            className="group/SliderButton w-[50px] h-[50px] flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 transition-all hover:border-brand bg-transparent [&_svg_path]:fill-gray"
            aria-label="Previous slide"
            disabled={slideIndex === 0}
            onClick={() => {
              carouselRef.current?.goBack();
            }}
          >
            <IconArrowLeftWhite />
          </button>
          <button
            type="button"
            className="group/SliderButton w-[50px] h-[50px] flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 transition-all hover:border-brand bg-transparent [&_svg_path]:fill-gray"
            aria-label="Next slide"
            disabled={slideIndex >= finalSlides.length - visibleSlides}
            onClick={() => {
              carouselRef.current?.goForward();
            }}
          >
            <IconArrowRightWhite />
          </button>
        </div>
      )}
      {isMobile && !isMobileMaterialSwatches ? (
        <div className="overflow-hidden">
          <div
            className={classNames(
              '-ml-5 -mr-5 pl-5 pr-5 flex overflow-scroll overflow-y-hidden',
              'max-[767px]:ml-0 max-[767px]:mr-0 max-[767px]:pl-5 max-[767px]:pr-5',
              '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
              'h-[425px]',
              {
                'overflow-scroll overflow-y-hidden flex':
                  variant === 'blog-slider' || variant === 'collections-slider',
              }
            )}
          >
            {finalSlides}
          </div>
        </div>
      ) : (
        <ForwardRefCarousel
          className="space-x-0 md:space-x-5"
          ref={carouselRef}
          showArrows={
            variant === 'material-swatches-sidebar' ? !hideNav : false
          }
          height={fullHeight ? '100%' : isDesktop ? '450px' : '400px'}
          heightMode="max"
          slidesToShow={visibleSlides}
          cellSpacing={showLeftAndRightPartially ? 5 : 20}
          cellAlign={showLeftAndRightPartially ? 'center' : undefined}
          wrapAround={
            showLeftAndRightPartially
              ? true
              : finalVisibleSlides < finalSlides.length
          }
          wrapMode={
            showLeftAndRightPartially || finalVisibleSlides < finalSlides.length
              ? 'wrap'
              : undefined
          }
          dragging={!showLeftAndRightPartially}
          swiping={!disableSliding}
          prevSlidesToScroll={showLeftAndRightPartially ? 1 : undefined}
          width="100%"
          renderCenterLeftControls={
            variant !== 'blog-slider'
              ? ({ previousSlide }) => (
                  <button
                    type="button"
                    className={classNames(
                      'group/SliderButton w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand !bg-white left-2 transform-[translateX(-50%)]',
                      '[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray',
                      'group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]',
                      variant === 'blog-slider' && 'rounded-none'
                    )}
                    aria-label="Previous slide"
                    onClick={previousSlide}
                  >
                    <IconArrowLeftWhite />
                  </button>
                )
              : undefined
          }
          renderCenterRightControls={
            variant !== 'blog-slider'
              ? ({ nextSlide }) => (
                  <button
                    type="button"
                    className={classNames(
                      'group/SliderButton w-[50px] h-[50px] rounded-[100px] !flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 [transition:all_.2s_ease-in] hover:border-brand !bg-white right-2 transform-[translateX(50%)]',
                      '[&_svg_path]:[transition:all_.2s_ease-in] [&_svg_path]:fill-gray',
                      'group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]',
                      variant === 'blog-slider' && 'rounded-none'
                    )}
                    aria-label="Next slide"
                    onClick={nextSlide}
                  >
                    <IconArrowRightWhite />
                  </button>
                )
              : undefined
          }
          beforeSlide={index => {
            if (typeof props.beforeSlideFn === 'function') {
              props.beforeSlideFn(index);
            }
          }}
          afterSlide={index => {
            if (variant === 'blog-slider') {
              setSlideIndex(index);
            }
            if (typeof props.afterSlideFn === 'function') {
              props.afterSlideFn(index);
            }
          }}
        >
          {finalSlides}
        </ForwardRefCarousel>
      )}
    </div>
  );
};

export default NewSliderComponent;
